/**
 * CtaSplit — horizontal band with headline left and CTA button right.
 * A subtle mid-page conversion prompt; less heavy than CtaBanner.
 *
 * Registered as: cta / cta-split
 */
import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type { SectionComponentProps } from "../../registry.js";

gsap.registerPlugin(ScrollTrigger);

interface CtaSplitProps {
  headline?: string;
  subheadline?: string;
  ctaLabel?: string;
  ctaTarget?: string;
  secondaryLabel?: string;
  secondaryTarget?: string;
}

export function CtaSplit({ props }: SectionComponentProps) {
  const {
    headline = "Ready to get started?",
    subheadline,
    ctaLabel = "Get a Quote",
    ctaTarget = "#quote",
    secondaryLabel,
    secondaryTarget,
  } = props as CtaSplitProps;

  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: { trigger: el, start: "top 84%", toggleActions: "play none none none" },
        defaults: { ease: "power2.out" },
      });
      tl.fromTo(".cs-text", { opacity: 0, x: -24 }, { opacity: 1, x: 0, duration: 0.6 })
        .fromTo(".cs-actions", { opacity: 0, x: 24 }, { opacity: 1, x: 0, duration: 0.6 }, "-=0.4");
    }, el);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="py-14 px-6 bg-muted/40 border-y border-border" id="cta">
      <div className="max-w-5xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div className="cs-text">
          <h2 className="text-xl md:text-2xl font-bold text-foreground">{headline}</h2>
          {subheadline && (
            <p className="text-muted-foreground text-sm mt-1 max-w-md">{subheadline}</p>
          )}
        </div>

        <div className="cs-actions flex items-center gap-3 flex-shrink-0">
          <a
            href={ctaTarget}
            className="inline-flex items-center justify-center rounded-[var(--radius)] bg-primary text-primary-foreground px-6 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity shadow-sm"
          >
            {ctaLabel}
          </a>
          {secondaryLabel && (
            <a
              href={secondaryTarget}
              className="inline-flex items-center justify-center rounded-[var(--radius)] border border-border px-6 py-2.5 text-sm font-medium text-foreground hover:bg-background transition-colors"
            >
              {secondaryLabel}
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
